import type { BasemapSlice, GeoDataStateCreator, BasemapStyle, GeoDataState } from './types'; // Importa GeoDataState

// Importa le immagini dei basemap
import osmPreview from '../assets/basemaps/osm.png';
import worldimageryPreview from '../assets/basemaps/world_imagery.png';
import worldtopoPreview from '../assets/basemaps/world_topo.png';
import ofmLiberty from '../assets/basemaps/ofm_liberty.png';
import ofmBright from '../assets/basemaps/ofm_bright.png';
import ofmPositron from '../assets/basemaps/ofm_positron.png';
import ofmDark from '../assets/basemaps/ofm_dark.png';

// Costanti per i basemap (spostate qui da useGeoData)
const defaultBasemapId = 'openfreemap-liberty'; // Definisco l'ID della basemap di default

const availableBasemapsData: BasemapStyle[] = [
  // Nuovi stili OpenFreeMap
  {
    id: "openfreemap-liberty",
    name: "OFM Liberty",
    styleUrl: "https://tiles.openfreemap.org/styles/liberty",
    type: "vector",
    imageUrl: ofmLiberty
  },
  {
    id: "openfreemap-bright",
    name: "OFM Bright",
    styleUrl: "https://tiles.openfreemap.org/styles/bright",
    type: "vector",
    imageUrl: ofmBright
  },
  {
    id: "openfreemap-positron",
    name: "OFM Positron",
    styleUrl: "https://tiles.openfreemap.org/styles/positron",
    type: "vector",
    imageUrl: ofmPositron
  },
  {
    id: "openfreemap-dark",
    name: "OFM Dark",
    styleUrl: "https://tiles.openfreemap.org/styles/dark",
    type: "vector",
    imageUrl: ofmDark
  },
  // Basemaps Raster Esistenti
  {
    id: 'world-imagery',
    name: 'World Imagery',
    type: 'raster',
    tilesUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    imageUrl: worldimageryPreview
  },
  {
    id: 'osm-raster',
    name: 'OpenStreetMap',
    type: 'raster',
    tilesUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    imageUrl: osmPreview
  },
  {
    id: 'world-topo',
    name: 'World Topo Map',
    type: 'raster',
    tilesUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    imageUrl: worldtopoPreview
  },
  // Rimosso: BasemapStyle con id: 'background-only'
];

const defaultBackgroundColor = '#f0f0f0'; // Colore di background di default

export const createBasemapSlice: GeoDataStateCreator<BasemapSlice> = (set, get) => ({
  availableBasemaps: availableBasemapsData,
  activeBasemapId: defaultBasemapId,
  useSolidBackground: false, // NUOVA proprietà
  currentBackgroundColor: defaultBackgroundColor, // Impostato al default
  currentBackgroundOpacity: 1, // NUOVA proprietà per l'opacità
  setBasemap: (id) => {
    const newBasemap = get().availableBasemaps.find(b => b.id === id);
    if (newBasemap) {
      set({
        activeBasemapId: id,
        useSolidBackground: false, // Disattiva lo sfondo solido quando si cambia basemap
        // currentBackgroundColor non viene più toccato qui
        currentBackgroundOpacity: 1, // Resetta l'opacità quando si cambia basemap
      });
    }
  },
  setActiveBackgroundColor: (color) => {
    set({ currentBackgroundColor: color });
  },
  setActiveBackgroundOpacity: (opacity) => {
    // Assicura che l'opacità sia tra 0 e 1
    const newOpacity = Math.max(0, Math.min(1, opacity));
    set({ currentBackgroundOpacity: newOpacity });
  },
  toggleUseSolidBackground: () => {
    set((state) => {
      const newUseSolidBackground = !state.useSolidBackground;
      let newCurrentBackgroundColor = state.currentBackgroundColor;
      const newCurrentBackgroundOpacity = state.currentBackgroundOpacity; // Mantiene l'opacità corrente
      if (newUseSolidBackground && newCurrentBackgroundColor === null) {
        newCurrentBackgroundColor = defaultBackgroundColor; // Imposta il colore di default se nullo
      }
      // Opzionale: resettare l'opacità a 1 quando si disattiva lo sfondo solido
      // if (!newUseSolidBackground) {
      //   newCurrentBackgroundOpacity = 1;
      // }
      return {
        useSolidBackground: newUseSolidBackground,
        currentBackgroundColor: newCurrentBackgroundColor,
        currentBackgroundOpacity: newCurrentBackgroundOpacity,
      };
    });
  },
});

// Selettore per ottenere l'URL dello stile della basemap attiva
export const selectActiveBasemapStyleUrl = (state: GeoDataState): string | undefined => {
  // Se useSolidBackground è true, non c'è URL di basemap tradizionale
  if (state.useSolidBackground) {
    return undefined;
  }
  const activeBasemap = state.availableBasemaps.find((b: BasemapStyle) => b.id === state.activeBasemapId);
  return activeBasemap?.type === 'vector' ? activeBasemap.styleUrl : activeBasemap?.tilesUrl;
};

// Nuovo selettore per ottenere l'intero oggetto BasemapStyle attivo
export const selectActiveBasemap = (state: GeoDataState): BasemapStyle | undefined => {
  // Se useSolidBackground è true, non c'è una basemap "attiva" nel senso tradizionale
  if (state.useSolidBackground) {
    return undefined;
  }
  return state.availableBasemaps.find((b: BasemapStyle) => b.id === state.activeBasemapId);
};

// Nuovo selettore per ottenere l'array di stili basemap disponibili
export const selectAvailableBasemapStyles = (state: GeoDataState): BasemapStyle[] => {
   return state.availableBasemaps;
};

// Selettore per ottenere il colore di background corrente
export const selectCurrentBackgroundColor = (state: GeoDataState): string | null => {
  return state.currentBackgroundColor;
};

// NUOVO selettore per useSolidBackground
export const selectUseSolidBackground = (state: GeoDataState): boolean => {
  return state.useSolidBackground;
};

// NUOVO selettore per currentBackgroundOpacity
export const selectCurrentBackgroundOpacity = (state: GeoDataState): number => {
  return state.currentBackgroundOpacity;
};